import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import MuiLink from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DialogConfirm from './DialogConfirm'
import ExtendDeadlineButton from './ExtendDeadlineButton'
import Timestamp from './Timestamp'
import useBilling from '../models/useBilling'
import { useNoticeLeadMinutes } from '../models/useDeadlineNotify'
import useNodeTasks from '../models/useNodeTasks'
import useTasks, { type ProvisionedTask } from '../models/useTasks'
import { DOCS_PROVISIONING_URL } from '../constants'
import { formatDuration, taskLifecycle, taskShapeLabel } from '../utilities/taskDeadline'
import { deadlineNotice } from '../utilities/deadlineNotice'
import {
  computeHeaderLine,
  nodeTaskCost,
  nodeTaskRow,
  nodeTaskStatus,
  sortNodeTaskRows,
  stopNotice,
  TASK_STOP_WAIT_SECONDS,
  type StopNotice,
} from '../utilities/computeNodePage'

// The list prints whole minutes, so the clock does not need to move faster.
const TICK_MS = 30_000
// Finer than the router's 10-minute compute-stop sweep, so a stop reaches the screen within one sweep.
const REFRESH_MS = 120_000
const NO_TASKS: ProvisionedTask[] = []
// The same form as `Timestamp`'s default (`full`), so the notice reads like the task list under it.
const formatStoppedAt = (d: Date): string =>
  d.toLocaleDateString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' })

/**
 * The compute part of a node page (nobgp/app#330): the running machine, its
 * controls, and every task the node has run.
 *
 * ⚠ Renders nothing for a node that has never run a task. That is every node a
 * person installed the agent on.
 *
 * ⚠⚠ STOP AND TERMINATE ARE DIFFERENT TOOLS (nobgp/app#337). Stop calls
 * `task_stop`: the machine stops and the node stays, offline, with its name,
 * labels and node storage, so this page stays open. Terminate calls
 * `deprovision_node`: the node is removed too, so this page closes. The task list
 * tells them apart by stop reason (`user_stop` and `user`).
 *
 * ⚠ Extend, Stop and Terminate go through `useTasks.setDeadline`, `useTasks.stop`
 * and `useTasks.deprovision`, so each act has one way to do it.
 */
export default function NodeComputeSection({
  nodeId,
  nodeName,
  networkId,
  canOperate,
}: Readonly<{ nodeId: string; nodeName: string; networkId?: string; canOperate: boolean }>) {
  const navigate = useNavigate()
  // ⚠ A stable empty array: a zustand v5 selector that returns a new `[]` loops.
  const tasks = useNodeTasks(s => (s.nodeId === nodeId ? s.tasks : NO_TASKS))
  const catalogue = useNodeTasks(s => s.catalogue)
  const extras = useNodeTasks(s => s.extras)
  const extrasError = useNodeTasks(s => s.extrasError)
  const loadError = useNodeTasks(s => s.error)
  const load = useNodeTasks(s => s.get)
  const deprovisioning = useTasks(s => s.deprovisioning)
  const deadlineChanging = useTasks(s => s.deadlineChanging)
  const stopping = useTasks(s => s.stopping)
  // ⚠ `canOperate` is control of the NODE (its owner, or an Owner/Admin of the node's org), from
  // the node page (app#373, docs/PERMISSIONS.md § 5 Node: "Stop, set deadline, deprovision its
  // compute — node owner or Admin"). It was `node.write`, any Member, who then clicked into the
  // router's refusal. ⚠⚠ The org is the node's TENANT, `node.ownerOrgId` (G-1, app#385):
  // `task_stop` and `task_deadline_set` ask that org, not the org of the node's network, so the
  // node page computes this with `useOrgRole(node.ownerOrgId)` (Benoit, 2026-09-26).
  const allowUnlimited = useBilling(s => s.plan?.computeDeadlineOptional) === true
  const [now, setNow] = useState(() => new Date())
  // The viewer's saved lead time, else 60 minutes (nobgp/app#354).
  const leadMinutes = useNoticeLeadMinutes()
  // ⚠ The error of THIS page's last action. `useTasks.error` is shared with the usage page,
  // so reading it directly would show a failure from another screen here.
  const [actionError, setActionError] = useState<string | null>(null)
  // What the last Stop did. It stays after the open task is gone, which is when it is needed.
  const [notice, setNotice] = useState<StopNotice | null>(null)
  // ⚠⚠ THE NODE ON SCREEN NOW, NOT THE ONE IN AN ACTION'S CLOSURE. The route reuses this component
  // when the reader opens another node, and Stop can take 35 s. After the await, `load` of the old
  // node would move `useNodeTasks` back to it, so the new node's section renders nothing until a
  // reload, and the old node's notice or error shows on the new node. Each action compares this ref
  // to the node it started on, and returns when they differ.
  // ⚠ Do NOT clear it on unmount. A Terminate that succeeds re-reads `useNode`, the deleted node
  // leaves `Node`, and this section unmounts BEFORE `terminate` reaches `navigate`. A cleared ref
  // would leave the reader on an empty page.
  const nodeRef = useRef(nodeId)
  nodeRef.current = nodeId

  useEffect(() => {
    void load(nodeId)
    setNotice(null)
    setActionError(null)
  }, [nodeId, load])

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  const rows = useMemo(
    () => sortNodeTaskRows(tasks.map(t => nodeTaskRow(t, taskShapeLabel(t, catalogue), now, extras))),
    [tasks, catalogue, now, extras],
  )
  // The newest open task. There should be at most one; the router refuses a second provision on a live name.
  const openTask = tasks.find(t => t.completedAt === null) ?? null
  const openRow = openTask ? rows.find(r => r.id === openTask.id) ?? null : null

  // ⚠ `tasks` is not in the realtime publication, so nothing pushes a stop to this page.
  // ⚠ The header chip (`useComputeNodes`, app#329) reads `useTasks`, not this store. Refresh both,
  // or the section shows a task as ended while the chip still says "stopping" for one more poll.
  useEffect(() => {
    if (!openTask) return
    const id = setInterval(() => {
      void load(nodeId)
      void useTasks.getState().get({ background: true })
    }, REFRESH_MS)
    return () => clearInterval(id)
  }, [openTask?.id, nodeId, load])

  if (tasks.length === 0) return null

  const extend = async (hours: number | null) => {
    if (!openTask) return
    const startNodeId = nodeId
    setActionError(null)
    // ⚠ Success is the call's own result. `useTasks.error` is shared, so a later read on the
    // usage page could set it after this call succeeded.
    const done = await useTasks.getState().setDeadline(openTask.id, hours)
    if (nodeRef.current !== startNodeId) return
    if (!done) setActionError(useTasks.getState().error ?? 'The deadline could not be changed.')
    await load(startNodeId)
  }

  const stop = async () => {
    if (!openTask) return
    const startNodeId = nodeId
    setActionError(null)
    setNotice(null)
    // ⚠ Success is the call's own result, for the reason `extend` gives.
    const outcome = await useTasks.getState().stop(openTask.id)
    if (nodeRef.current !== startNodeId) return
    if (!outcome) {
      // ⚠ The router's refusal, word for word.
      setActionError(useTasks.getState().error ?? 'The node could not be stopped.')
    } else {
      setNotice(stopNotice(outcome, nodeName, formatStoppedAt))
    }
    await load(startNodeId)
  }

  const terminate = async () => {
    if (!openTask) return
    const startNodeId = nodeId
    setActionError(null)
    setNotice(null)
    const done = await useTasks.getState().deprovision(openTask.id)
    // ⚠ Also before `navigate`: a reader who opened another node stays on it.
    if (nodeRef.current !== startNodeId) return
    if (!done) {
      setActionError(useTasks.getState().error ?? 'The node could not be terminated.')
      await load(startNodeId)
      return
    }
    // The node is soft-deleted with its task, so this page has nothing left to show.
    navigate(networkId ? `/networks/${networkId}` : '/nodes')
  }

  const showExtras = extras !== null
  // ⚠ Before the stop: the open task. After it: the newest task, only when no task is open, and
  // only from the recorded reason (`get_node_tasks`). Without the RPC there is no stopped notice.
  const newestTask = rows.length > 0 ? tasks.find(t => t.id === rows[0].id) ?? null : null
  const limitNotice = openTask
    ? deadlineNotice(taskLifecycle(openTask, now), null, leadMinutes)
    : newestTask
      ? deadlineNotice(taskLifecycle(newestTask, now), extras?.get(newestTask.id)?.stopReason, leadMinutes)
      : null
  // ⚠ One act at a time on a task: Stop and Terminate end it, and Extend moves a deadline it may no longer have.
  // A pending Extend is in `busy` too, so Stop and Terminate stay disabled while the deadline moves.
  const busy = deprovisioning !== null || stopping !== null || deadlineChanging !== null
  const isStopping = openTask !== null && stopping === openTask.id

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">Compute</Typography>
        {openRow && <Chip size="small" variant="outlined" color="success" label={`Compute · ${openRow.size}`} />}
      </Stack>

      {limitNotice && (
        <Alert severity={limitNotice.severity} sx={{ mb: 1 }}>
          {limitNotice.kind === 'stopped-at-limit'
            ? `${limitNotice.text}. To start it again, create a compute node with the name ${nodeName}.`
            : `${limitNotice.text}. Use Give more time to keep it running.`}
        </Alert>
      )}

      {openTask && openRow && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {computeHeaderLine(openRow, now)}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
            <ExtendDeadlineButton
              lifecycle={taskLifecycle(openTask, now)}
              now={now}
              allowUnlimited={allowUnlimited}
              disabled={!canOperate || busy}
              busy={deadlineChanging === openTask.id}
              onPick={hours => void extend(hours)}
            />
            <DialogConfirm
              color="warning"
              action="Stop"
              title={`Stop ${nodeName}?`}
              disabled={!canOperate || busy}
              dialogChildren={
                <Typography variant="body1" component="div">
                  This stops the machine now and stops its billing.
                  <ul>
                    <li>The container, its running processes and everything on its disk are deleted.</li>
                    <li>
                      The node stays in your network, offline, with its name, labels, permissions and node storage
                      (its <code>node/</code> area).
                    </li>
                    <li>
                      To start it again, create a compute node with the name{' '}
                      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                        {nodeName}
                      </Box>
                      .
                    </li>
                  </ul>
                  This can take up to {TASK_STOP_WAIT_SECONDS} seconds.
                </Typography>
              }
              onConfirm={() => void stop()}
            >
              <Button size="small" color="warning" disabled={!canOperate || busy}>
                {isStopping ? 'Stopping…' : 'Stop'}
              </Button>
            </DialogConfirm>
            <DialogConfirm
              color="error"
              action="Terminate"
              title={`Terminate ${nodeName}?`}
              disabled={!canOperate || busy}
              dialogChildren={
                <Typography variant="body1" component="div">
                  This stops the machine now and stops its billing.
                  <ul>
                    <li>The container, its running processes and everything on its disk are deleted.</li>
                    <li>The node is removed from your network, so this page closes.</li>
                    <li>
                      The node storage (its <code>node/</code> area) is kept for 30 days. Provision the same
                      name again in that time to get the node back with its storage, labels and permissions.
                    </li>
                  </ul>
                  This can take up to 35 seconds.
                </Typography>
              }
              onConfirm={() => void terminate()}
            >
              <Button size="small" color="error" disabled={!canOperate || busy}>
                {deprovisioning === openTask.id ? 'Terminating…' : 'Terminate'}
              </Button>
            </DialogConfirm>
          </Stack>
          {isStopping && (
            // ⚠ The router answers only after the node reads offline, up to 35 s. Without this line a
            // reader sees a disabled button for half a minute and presses again.
            <Box sx={{ mb: 1 }}>
              <LinearProgress />
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                Stopping the machine and waiting for the node to go offline. This can take up to{' '}
                {TASK_STOP_WAIT_SECONDS} seconds.
              </Typography>
            </Box>
          )}
        </>
      )}

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 1 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <MuiLink href={DOCS_PROVISIONING_URL} target="_blank" rel="noopener noreferrer" variant="body2">
        What is on this machine?
      </MuiLink>

      {(actionError || loadError) && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {actionError ?? loadError}
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Tasks
      </Typography>
      {/* ⚠ Two different facts: the RPC is not deployed yet, or it is and it failed.
          "Not available yet" is false for the second. */}
      {!showExtras && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {extrasError
            ? 'Cost and how each task ended could not be loaded.'
            : 'Cost and how each task ended are not available yet.'}
        </Typography>
      )}
      {/* ⚠⚠ TWO LINES PER TASK, NOT A TABLE. The node page is a side panel about 185 px wide at
          its default width, and a seven-column table showed three columns and half a fourth,
          with nothing to hint that the rest needed a horizontal scroll. Each line carries the
          same facts the table did: when and how long, then the deadline, the cost and the status. */}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
        {rows.map(row => (
          <Box component="li" key={row.id}>
            <Typography variant="body2">
              {/* The same date form as the Details block (`Timestamp`'s default). */}
              <Timestamp date={row.startedAt} /> → {row.endedAt ? <Timestamp date={row.endedAt} /> : 'now'} ·{' '}
              {row.size} · {formatDuration(row.durationMs)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {nodeTaskStatus(row)} ·{' '}
              {row.deadlineAt ? (
                <>
                  deadline <Timestamp date={row.deadlineAt} />
                </>
              ) : (
                'no time limit'
              )}
              {showExtras && <> · {nodeTaskCost(row)}</>}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
